/**
 * @alias class.AccessibilityMixin
 * @description Base class with accessibility features
 */
const AccessibilityMixin = (superclass) =>
  class extends superclass {
    set frame(value) {
      this._frame = value;
      this._updateAccessibilityElement();
    }

    get frame() {
      return this._frame;
    }

    set y(value) {
      this._y = value;
      this._updateAccessibilityElement();
    }

    get y() {
      return this._y;
    }

    set x(value) {
      this._x = value;
      this._updateAccessibilityElement();
    }

    get x() {
      return this._x;
    }

    set parentContainer(value) {
      this._parentContainer = value;
      this._updateAccessibilityElement();
    }

    get parentContainer() {
      return this._parentContainer;
    }

    set scaleX(value) {
      super.scaleX = value;
      this._updateAccessibilityElement();
    }

    get scaleX() {
      return super.scaleX;
    }

    set scaleY(value) {
      super.scaleY = value;
      this._updateAccessibilityElement();
    }

    get scaleY() {
      return super.scaleY;
    }

    set visible(value) {
      super.visible = value;
      this._updateAccessibilityElement();
    }

    get visible() {
      return super.visible;
    }

    set scrollFactorX(value) {
      this._scrollFactorX = value;
      this._updateAccessibilityElement();
    }

    get scrollFactorX() {
      return this._scrollFactorX ?? 1;
    }

    set scrollFactorY(value) {
      this._scrollFactorY = value;
      this._updateAccessibilityElement();
    }

    get scrollFactorY() {
      return this._scrollFactorY ?? 1;
    }

    set originX(value) {
      this._originX = value;
      this._updateAccessibilityElement();
    }

    get originX() {
      return this._originX ?? 0.5;
    }

    set originY(value) {
      this._originY = value;
      this._updateAccessibilityElement();
    }

    get originY() {
      return this._originY ?? 0.5;
    }

    _handleAccessibilityFocus() {}

    _handleAccessibilityBlur() {}

    _handleAccessibilitySelect() {
      this.emit('select', this);
    }

    setInteractive(hitArea, hitAreaCallback, dropZone) {
      super.setInteractive(hitArea, hitAreaCallback, dropZone);
      if (hitArea?.ariaLabel) {
        this.ariaLabel = hitArea.ariaLabel;
        if (this._accessibilityElement) {
          this._accessibilityElement.ariaLabel = this.ariaLabel;
        }
      }
      if (hitArea?.ariaFollow) {
        this.ariaFollow = true;
      }
      if (this.ariaLabel) {
        this._isAccessible = true;
        this._updateAccessibilityElement();
      }
    }

    disableInteractive() {
      super.disableInteractive();
      this._isAccessible = false;
      this._updateAccessibilityElement();
    }

    syncAccessibilityElement() {
      this._updateAccessibilityElement();
    }

    _getAccessibilityHitarea() {
      const mat = this.getWorldTransformMatrix();
      return {
        x:
          mat.getX(0, 0) -
          this.displayWidth * this.originX -
          this.scene?.cameras?.main?.scrollX * this.scrollFactorX,
        y:
          mat.getY(0, 0) -
          this.displayHeight * this.originY -
          this.scene?.cameras?.main?.scrollY * this.scrollFactorY,
        width: this.displayWidth,
        height: this.displayHeight,
      };
    }

    _getClampedAccessibilityHitarea() {
      const limits = this?.scene?.game?.viewportController;
      if (!limits) return { x: 0, y: 0, width: 0, height: 0 };

      const rect = this._getAccessibilityHitarea();
      if (rect.x < limits.x) {
        rect.width -= limits.x - rect.x;
        rect.x = limits.x;
      }
      if (rect.x + rect.width > limits.x + limits.width) {
        rect.width = limits.x + limits.width - rect.x;
      }
      if (rect.y < limits.y) {
        rect.height -= limits.y - rect.y;
        rect.y = limits.y;
      }
      if (rect.y + rect.height > limits.y + limits.height) {
        rect.height = limits.y + limits.height - rect.y;
      }
      return rect;
    }

    _createAccessibilityElement() {
      if (this._accessibilityElement || !this.ariaLabel || !this.scene?.game?.accessibility) return;

      const rect = this._getClampedAccessibilityHitarea();
      if (rect.width <= 0 || rect.height <= 0 || !this.visible) {
        return;
      }

      this._accessibilityElement = this.scene.game.accessibility.addItem(
        this.id ?? this.ariaLabel,
        this.ariaLabel,
        rect
      );

      this._accessibilityElement.on('focus', this._handleAccessibilityFocus, this);
      this._accessibilityElement.on('blur', this._handleAccessibilityBlur, this);
      this._accessibilityElement.on('select', this._handleAccessibilitySelect, this);
    }

    _removeAccessibilityElement() {
      if (!this._accessibilityElement) return;

      this._accessibilityElement.off('focus', this._handleAccessibilityFocus, this);
      this._accessibilityElement.off('blur', this._handleAccessibilityBlur, this);
      this._accessibilityElement.off('select', this._handleAccessibilitySelect, this);
      this._accessibilityElement.destroy();
      this._accessibilityElement = null;
    }

    _updateAccessibilityElement() {
      if (!this._isAccessible) {
        this._removeAccessibilityElement();
        return;
      }

      const rect = this._getClampedAccessibilityHitarea();
      if (rect.width <= 0 || rect.height <= 0 || !this.visible) {
        this._removeAccessibilityElement();
      } else if (this.visible) {
        this._createAccessibilityElement();
      }

      if (this._accessibilityElement) {
        if (Math.abs(this._accessibilityElement.x - rect.x) >= 1) {
          this._accessibilityElement.x = rect.x;
        }
        if (Math.abs(this._accessibilityElement.y - rect.y) >= 1) {
          this._accessibilityElement.y = rect.y;
        }
        if (Math.abs(this._accessibilityElement.width - rect.width) >= 1) {
          this._accessibilityElement.width = rect.width;
        }
        if (Math.abs(this._accessibilityElement.height - rect.height) >= 1) {
          this._accessibilityElement.height = rect.height;
        }
      }

      if (this.ariaFollow && !this._accessibilityTimer) {
        this._accessibilityTimer = this.scene?.time.delayedCall(500, () => {
          this._accessibilityTimer = null;
          this._updateAccessibilityElement();
        });
      }
    }

    destroy(fromScene = false) {
      this._removeAccessibilityElement();
      super.destroy && super.destroy(fromScene);
    }
  };

export { AccessibilityMixin };
